import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { PenNib, X } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * EditShipmentDialog
 * Lets operators update shipment configuration after creation:
 *  - basic info (name, customer, cargo type, driver)
 *  - thresholds (temp_min/max, humidity_min/max)
 *  - origin / destination addresses
 *  - references (PO, BOL, etc.)
 *
 * Parties / facilities / geofences continue to be managed in their existing
 * dedicated tabs (richer pickers, snapshot hydration).
 */
export default function EditShipmentDialog({ shipment, onClose, onSaved }) {
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: shipment.name || "",
    customer: shipment.customer || "",
    cargo_type: shipment.cargo_type || "",
    driver_name: shipment.driver_name || "",
    temp_min: shipment.temp_min ?? 32,
    temp_max: shipment.temp_max ?? 40,
    humidity_min: shipment.humidity_min ?? 20,
    humidity_max: shipment.humidity_max ?? 80,
    origin_address: shipment.origin?.address || "",
    destination_address: shipment.destination?.address || "",
    eta: shipment.eta ? shipment.eta.slice(0, 16) : "",
  });
  const [references, setReferences] = useState(
    (shipment.references || []).map((r) => ({ ...r })),
  );

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const updateRef = (i, k, v) =>
    setReferences((rs) => rs.map((r, idx) => (idx === i ? { ...r, [k]: v } : r)));

  const addRef = () => setReferences((rs) => [...rs, { type: "PO", value: "" }]);
  const removeRef = (i) => setReferences((rs) => rs.filter((_, idx) => idx !== i));

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        name: form.name,
        customer: form.customer || null,
        cargo_type: form.cargo_type || null,
        driver_name: form.driver_name || null,
        temp_min: Number(form.temp_min),
        temp_max: Number(form.temp_max),
        humidity_min: Number(form.humidity_min),
        humidity_max: Number(form.humidity_max),
        origin: { ...(shipment.origin || {}), address: form.origin_address },
        destination: { ...(shipment.destination || {}), address: form.destination_address },
        references: references.filter((r) => (r.value || "").trim()),
      };
      if (form.eta) payload.eta = new Date(form.eta).toISOString();
      await axios.put(`${API}/shipments/${shipment.id}`, payload);
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update shipment");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4"
      onClick={() => !saving && onClose?.()}>
      <Card className="bg-zinc-950 border-zinc-800 w-full max-w-2xl max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()} data-testid="edit-shipment-dialog">
        <div className="flex items-center justify-between p-5 border-b border-zinc-800">
          <h3 className="text-sm font-semibold text-zinc-100 flex items-center gap-2">
            <PenNib size={14} className="text-teal-400" /> Edit Shipment
          </h3>
          <button onClick={() => !saving && onClose?.()}
            className="text-zinc-500 hover:text-zinc-300"
            data-testid="edit-shipment-close">
            <X size={16} />
          </button>
        </div>

        <ScrollArea className="flex-1">
          <div className="p-5 space-y-5">
            {/* Basic Info */}
            <section>
              <h4 className="text-[10px] uppercase tracking-wider text-zinc-500 mb-2">Basic Info</h4>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Name" required>
                  <Input value={form.name} onChange={(e) => update("name", e.target.value)}
                    data-testid="edit-name" className="bg-zinc-900 border-zinc-800 text-sm h-9" />
                </Field>
                <Field label="Customer">
                  <Input value={form.customer} onChange={(e) => update("customer", e.target.value)}
                    data-testid="edit-customer" className="bg-zinc-900 border-zinc-800 text-sm h-9" />
                </Field>
                <Field label="Cargo Type">
                  <Input value={form.cargo_type} onChange={(e) => update("cargo_type", e.target.value)}
                    data-testid="edit-cargo" className="bg-zinc-900 border-zinc-800 text-sm h-9" />
                </Field>
                <Field label="Driver">
                  <Input value={form.driver_name} onChange={(e) => update("driver_name", e.target.value)}
                    data-testid="edit-driver" className="bg-zinc-900 border-zinc-800 text-sm h-9" />
                </Field>
              </div>
            </section>

            {/* Thresholds */}
            <section>
              <h4 className="text-[10px] uppercase tracking-wider text-zinc-500 mb-2">Thresholds</h4>
              <div className="grid grid-cols-4 gap-3">
                <Field label="Temp Min (°F)">
                  <Input type="number" value={form.temp_min}
                    onChange={(e) => update("temp_min", e.target.value)}
                    data-testid="edit-temp-min" className="bg-zinc-900 border-zinc-800 text-sm h-9 font-mono" />
                </Field>
                <Field label="Temp Max (°F)">
                  <Input type="number" value={form.temp_max}
                    onChange={(e) => update("temp_max", e.target.value)}
                    data-testid="edit-temp-max" className="bg-zinc-900 border-zinc-800 text-sm h-9 font-mono" />
                </Field>
                <Field label="Humidity Min (%)">
                  <Input type="number" value={form.humidity_min}
                    onChange={(e) => update("humidity_min", e.target.value)}
                    data-testid="edit-hum-min" className="bg-zinc-900 border-zinc-800 text-sm h-9 font-mono" />
                </Field>
                <Field label="Humidity Max (%)">
                  <Input type="number" value={form.humidity_max}
                    onChange={(e) => update("humidity_max", e.target.value)}
                    data-testid="edit-hum-max" className="bg-zinc-900 border-zinc-800 text-sm h-9 font-mono" />
                </Field>
              </div>
            </section>

            {/* Locations */}
            <section>
              <h4 className="text-[10px] uppercase tracking-wider text-zinc-500 mb-2">Locations</h4>
              <div className="grid grid-cols-1 gap-3">
                <Field label="Origin Address">
                  <Input value={form.origin_address}
                    onChange={(e) => update("origin_address", e.target.value)}
                    data-testid="edit-origin" className="bg-zinc-900 border-zinc-800 text-sm h-9" />
                </Field>
                <Field label="Destination Address">
                  <Input value={form.destination_address}
                    onChange={(e) => update("destination_address", e.target.value)}
                    data-testid="edit-destination" className="bg-zinc-900 border-zinc-800 text-sm h-9" />
                </Field>
                <Field label="ETA">
                  <Input type="datetime-local" value={form.eta}
                    onChange={(e) => update("eta", e.target.value)}
                    data-testid="edit-eta" className="bg-zinc-900 border-zinc-800 text-sm h-9 font-mono" />
                </Field>
              </div>
              <p className="text-[10px] text-zinc-600 mt-2">
                Facility / geofence pinning happens from the existing "Parties" and "Route" tabs,
                so addresses, lat/lng, and arrival-detection settings stay in sync.
              </p>
            </section>

            {/* References */}
            <section>
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-[10px] uppercase tracking-wider text-zinc-500">References</h4>
                <Button onClick={addRef} variant="outline" size="sm"
                  className="h-7 text-[11px] border-zinc-700"
                  data-testid="edit-add-ref">
                  + Add reference
                </Button>
              </div>
              {references.length === 0 && (
                <p className="text-[11px] text-zinc-600 italic">No references yet.</p>
              )}
              <div className="space-y-2">
                {references.map((r, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <select value={r.type}
                      onChange={(e) => updateRef(i, "type", e.target.value)}
                      data-testid={`edit-ref-type-${i}`}
                      className="bg-zinc-900 border border-zinc-800 text-zinc-200 text-xs font-mono rounded-sm h-9 px-2">
                      {["PO", "Order", "BOL", "AWB", "Container", "Load", "Pickup",
                        "Manifest", "Delivery Order", "Master Tracking", "Shipment",
                        "Work Order", "Customer Reference", "Custom"].map((t) => (
                        <option key={t} value={t}>{t}</option>
                      ))}
                    </select>
                    <Input value={r.value || ""} placeholder="value"
                      onChange={(e) => updateRef(i, "value", e.target.value)}
                      data-testid={`edit-ref-value-${i}`}
                      className="bg-zinc-900 border-zinc-800 text-sm h-9 font-mono flex-1" />
                    <button onClick={() => removeRef(i)}
                      className="text-zinc-500 hover:text-red-400"
                      data-testid={`edit-ref-remove-${i}`}
                      aria-label="Remove reference">
                      <X size={14} />
                    </button>
                  </div>
                ))}
              </div>
            </section>
          </div>
        </ScrollArea>

        <div className="flex justify-end gap-2 p-4 border-t border-zinc-800">
          <Button variant="outline" disabled={saving} onClick={onClose}
            className="border-zinc-700 text-xs h-9"
            data-testid="edit-shipment-cancel">
            Cancel
          </Button>
          <Button disabled={saving || !form.name.trim()} onClick={handleSave}
            className="bg-teal-500 hover:bg-teal-600 text-zinc-950 text-xs h-9 font-semibold"
            data-testid="edit-shipment-save">
            {saving ? "Saving…" : "Save Changes"}
          </Button>
        </div>
      </Card>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <Label className="text-[10px] uppercase tracking-wider text-zinc-400">
        {label}{required && <span className="text-red-400 ml-0.5">*</span>}
      </Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
